/**
 * Created by Ning Jiang on 2/21/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {WildNudgeGameConfig} from "../WildNudgeGameConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";

export class SymbolBurstAnimator extends BaseView {

    public static readonly PLAY_STACKED_SMOKE:string = "symbolBurstAnimator_playStackedSmoke";
    public static readonly PLAY_SINGLE_SMOKE:string = "symbolBurstAnimator_playSingleSmoke";

    private static _layer:ScalableLayer;
    private static _winFires:TimelineSprite[];
    private static _winTextTls:TimelineLite[] = [];
    private static _idleWinText:PIXI.BitmapText;

    constructor() {
        super();
        SymbolBurstAnimator._layer = StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.WIN_PRESENTATIONS.name);
        SymbolBurstAnimator._winFires = [];
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, SymbolBurstAnimator.PLAY_STACKED_SMOKE, (event:GameEvent) => this.playStackedSmoke(event.params[0], event.params[1]));
        EventHandler.addEventListener(this, SymbolBurstAnimator.PLAY_SINGLE_SMOKE, (event:GameEvent) => this.playSingleSmoke(event.params[0], event.params[1]));
    }

    private playStackedSmoke(reelId:number, symbolId:number):void {
        // Logger.logDev(`smoke reelId = ${reelId}, symbolId = ${symbolId}`);
        const smoke:TimelineSprite = new TimelineSprite(GameResources.getTextures("wild_smokeBurst"), 20);
        smoke.anchor.set(0.5, 0.9);
        smoke.scale.set(2, 2);
        smoke.blendMode = PIXI.BLEND_MODES.ADD;
        const position:[number, number] = Helper.getSymbolPositions(reelId, symbolId);
        smoke.position.set(position[0], position[1]);
        smoke.getAnimation([0], () => {}, () => {
            SymbolBurstAnimator._layer.removeChild(smoke);
        });
        SymbolBurstAnimator._layer.addChild(smoke);
    }

    private playSingleSmoke(reelId:number, symbolId:number):void {
        const smoke:TimelineSprite = new TimelineSprite(GameResources.getTextures("steam"), 60);
        smoke.scale.set(0.66, 0.66);
        smoke.blendMode = PIXI.BLEND_MODES.ADD;
        const position:[number, number] = Helper.getSymbolPositions(reelId, symbolId);
        smoke.position.set(position[0] - GameConfig.instance.SYMBOL_WIDTH * 0.5 + 2, position[1] - GameConfig.instance.SYMBOL_HEIGHT * 0.5 - 32);
        smoke.getAnimation([0], () => {}, () => {
            SymbolBurstAnimator._layer.removeChild(smoke);
        });
        SymbolBurstAnimator._layer.addChild(smoke);
    }

    public static playFireWin(reelId:number, symbolId:number, frames:number[] = [0], fps:number = 30, alpha:number = 1, isIdle:boolean = false):Timeline {
        const resourceName:string = isIdle ? "idle_fire_win" : "fire_win";
        const fire:TimelineSprite = new TimelineSprite(GameResources.getTextures(resourceName), fps);
        fire.alpha = alpha;
        fire.anchor.set(0.5, 0.5);
        fire.blendMode = PIXI.BLEND_MODES.ADD;
        const positions:[number, number] = Helper.getSymbolPositions(reelId, symbolId);
        fire.position.set(positions[0], positions[1]);
        this._winFires.push(fire);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            fire.getAnimation(frames),
            () => {
                this._layer.addChild(fire);
            }
        ]);
        timeline.add(() => {
            this._layer.removeChild(fire);
            ArrayHelper.removeFirstMatchElement(this._winFires, fire);
        });
        return timeline;
    }

    public static playIdleFireWin(reelId:number, symbolId:number, frames:number[] = [0], fps:number = 30, alpha:number = 1):Timeline {
        return SymbolBurstAnimator.playFireWin(reelId, symbolId, frames, fps, alpha, true);
    }

    private static createWinText(reelId:number, symbolId:number, winText:string):PIXI.BitmapText {
        const text = new PIXI.BitmapText(winText, {
            fontName : "NumbersHotNudge",
            fontSize : 12,
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        text.letterSpacing = -10;
        (<PIXI.Point>text.anchor).set(0.5, 0.5);
        // text.updateTransform();
        if(text.textWidth > 200) {
            const winScale = MathHelper.roundToDecimals(200 / text.textWidth, 2);
            text.scale.set(winScale);
        }
        const positions:[number, number] = Helper.getSymbolPositions(reelId, symbolId);
        text.position.set(positions[0], positions[1]);

        return text;
    }

    public static playWinText(reelId:number, symbolId:number, winText:string):Timeline {
        const text = SymbolBurstAnimator.createWinText(reelId, symbolId, winText);
        const ani = new TimelineLite({paused : true});
        ani.add(() => {
            ArrayHelper.removeFirstMatchElement(this._winTextTls, ani);
            StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.WIN_TEXTS.name).addChild(text);
        });
        ani.add([
            TweenLite.to(text, 1, {y : text.y - 100}),
            TweenLite.to(text, 0.8, {alpha : 0, delay : 0.2})
        ], `+=0.3`);
        ani.add(() => {
            text.parent.removeChild(text);
            text.destroy();
        });
        this._winTextTls.push(ani);

        const timeline = new TimelineLite();
        timeline.add(() => {
            ani.play();
        });

        return timeline;
    }

    public static playIdleWinText(reelId:number, symbolId:number, winText:string):void {
        SymbolBurstAnimator.abortIdleWinText();
        const text = SymbolBurstAnimator.createWinText(reelId, symbolId, winText);
        StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.WIN_TEXTS.name).addChild(text);
        SymbolBurstAnimator._idleWinText = text;
    }

    public static abortWin():void {
        while(SymbolBurstAnimator._winFires.length > 0) {
            const fire:TimelineSprite = SymbolBurstAnimator._winFires.pop()!;
            SymbolBurstAnimator._layer.removeChild(fire);
        }
        SymbolBurstAnimator._winTextTls.forEach(tl => {
            tl.play();
        });
        SymbolBurstAnimator.abortIdleWinText();
    }

    private static abortIdleWinText():void {
        if(SymbolBurstAnimator._idleWinText && SymbolBurstAnimator._idleWinText.transform) {
            if(SymbolBurstAnimator._idleWinText.parent) {
                SymbolBurstAnimator._idleWinText.parent.removeChild(SymbolBurstAnimator._idleWinText);
            }
            SymbolBurstAnimator._idleWinText.destroy();
        }
    }
}